import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
} from 'recharts';
import { TrendingUp, Assessment, People, FolderOpen, AccountBalance } from '@mui/icons-material';
import { BASE_URL } from '../config';

interface Risorsa {
  id: number;
  nome: string;
  cognome: string;
  struttura: string;
}

interface PianificazioneDettaglio {
  id_risorsa: number;
  cognome: string;
  nome: string;
  struttura: string;
  id_progetto: number | null;
  progetto_titolo: string | null;
  pm: string | null;
  cliente: string | null;
  mese: number | null;
  percentuale: number | null;
}

interface DashboardData {
  allocazione_media: number;
  top_10_progetti: {
    id: number;
    titolo: string;
    totale_allocazione: number;
  }[];
  andamento_mensile: {
    mese: number;
    allocazione_media: number;
  }[];
  statistiche: {
    totale_risorse: number;
    totale_progetti: number;
    progetti_in_uso: number;
    totale_allocazioni: number;
  };
  distribuzione_struttura: {
    struttura: string;
    numero_risorse: number;
  }[];
  budget_residuo_top: {
    titolo: string;
    budget_residuo_gg_uu: number;
    cliente: string;
  }[];
  pianificazione_dettagliata: PianificazioneDettaglio[];
  anno: number;
}

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF6B9D', '#C0C0C0', '#FFD700'
];

const MESI_LABELS: { [key: number]: string } = {
  1: 'GEN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAG', 6: 'GIU',
  7: 'LUG', 8: 'AGO', 9: 'SET', 10: 'OTT', 11: 'NOV', 12: 'DIC'
};

const DashboardPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [selectedRisorse, setSelectedRisorse] = useState<number[]>([]);
  const [selectedMesi, setSelectedMesi] = useState<number[]>(Array.from({ length: 12 }, (_, i) => i + 1));
  const [selectedStruttura, setSelectedStruttura] = useState<string | null>(null);
  const [selectedProgetto, setSelectedProgetto] = useState<number | null>(null);

  const { user } = useAuth();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const allMesi = Array.from({ length: 12 }, (_, i) => i + 1);

  const strutture = useMemo(() =>
    Array.from(new Set(risorse.map(r => r.struttura).filter(Boolean))).sort(),
    [risorse]
  );

  const handleStrutturaFilterChange = (struttura: string) => {
    if (!struttura) {
      setSelectedStruttura(null);
      setSelectedRisorse(risorse.map(r => r.id));
    } else {
      setSelectedStruttura(struttura);
      setSelectedRisorse(risorse.filter(r => r.struttura === struttura).map(r => r.id));
    }
  };

  // Carica lista risorse
  useEffect(() => {
    loadRisorse();
  }, []);

  // Inizializza i mesi di default quando cambia l'anno - tutti i 12 mesi
  useEffect(() => {
    setSelectedMesi(allMesi);
  }, [selectedYear]);

  useEffect(() => {
    if (selectedRisorse.length > 0 && selectedMesi.length > 0) {
      loadDashboardData();
    } else if (selectedMesi.length === 0 || selectedRisorse.length === 0) {
      // Se non ci sono mesi o risorse selezionate, azzera i dati
      setDashboardData(null);
    }
  }, [selectedYear, selectedRisorse, selectedMesi]);

  const loadRisorse = async () => {
    try {

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/risorse`, { headers });

      //const response = await fetch(`${BASE_URL}/risorse`);

      if (!response.ok) {
        throw new Error('Errore nel caricamento delle risorse');
      }
      const data = await response.json();
      const risorseAttive = data.filter((r: any) => r.enabled);

      // Filtra risorse in base alle strutture dell'utente
      let risorseFiltrate = risorseAttive;
      if (user && !user.hasTutteLeStrutture) {
        risorseFiltrate = risorseAttive.filter((r: Risorsa) =>
          user.strutture.includes(r.struttura || '')
        );
      }

      setRisorse(risorseFiltrate);
      // Preseleziona tutte le risorse filtrate
      setSelectedRisorse(risorseFiltrate.map((r: Risorsa) => r.id));
    } catch (err) {
      console.error('Error loading risorse:', err);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const risorseParam = selectedRisorse.join(',');
      const mesiParam = selectedMesi.join(',');

      const url = `${BASE_URL}/dashboard/${selectedYear}?risorse=${risorseParam}&mesi=${mesiParam}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error('Errore nel caricamento dei dati');
      }
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Errore nel caricamento della dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRisorseChange = (event: any) => {
    const value = event.target.value;
    setSelectedRisorse(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleMesiChange = (event: any) => {
    const value = event.target.value;
    setSelectedMesi(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleSelectAll = () => {
    // Seleziona tutte le risorse già filtrate per l'utente
    setSelectedRisorse(risorse.map(r => r.id));
  };

  const handleDeselectAll = () => {
    setSelectedRisorse([]);
  };

  const handleSelectAllMesi = () => {
    setSelectedMesi(allMesi);
  };

  const handleDeselectAllMesi = () => {
    setSelectedMesi([]);
  };

  const handleStrutturaClick = (data: any) => {
    // Per il BarChart, l'evento click passa direttamente il payload
    const struttura = data?.struttura || data?.payload?.struttura;

    if (struttura) {
      if (selectedStruttura === struttura) {
        // Se clicco sulla stessa struttura, deseleziona
        setSelectedStruttura(null);
        // Ripristina tutte le risorse
        setSelectedRisorse(risorse.map(r => r.id));
      } else {
        // Seleziona la nuova struttura e filtra le risorse
        setSelectedStruttura(struttura);
        const risorseStruttura = risorse.filter(r => r.struttura === struttura);
        setSelectedRisorse(risorseStruttura.map(r => r.id));
      }
    }
  };

  const handleProgettoClick = (data: any) => {
    if (data && data.id) {
      if (selectedProgetto === data.id) {
        // Se clicco sullo stesso progetto, deseleziona
        setSelectedProgetto(null);
      } else {
        // Seleziona il nuovo progetto
        setSelectedProgetto(data.id);
      }
    }
  };

  const formatAllocazione = (value: number) => {
    return `${parseFloat(value.toString()).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Prepara dati per il grafico a torta (top 10 progetti) solo se ci sono dati
  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Header con filtri anno, mesi e risorse */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            Dashboard Dirigenziale
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box>
              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Risorse</InputLabel>
                <Select
                  multiple
                  value={selectedRisorse}
                  onChange={handleRisorseChange}
                  input={<OutlinedInput label="Risorse" />}
                  renderValue={(selected) => `${selected.length} risorse selezionate`}
                >
                  {risorse.map((risorsa) => (
                    <MenuItem key={risorsa.id} value={risorsa.id}>
                      <Checkbox checked={selectedRisorse.indexOf(risorsa.id) > -1} />
                      <ListItemText primary={`${risorsa.cognome} ${risorsa.nome}`} secondary={risorsa.struttura} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAll}
                  sx={{ textTransform: 'none' }}
                >
                  Seleziona tutti
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDeselectAll}
                  sx={{ textTransform: 'none' }}
                >
                  Deseleziona tutti
                </Button>
              </Box>
            </Box>
            <Box>
              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Mesi</InputLabel>
                <Select
                  multiple
                  value={selectedMesi}
                  onChange={handleMesiChange}
                  input={<OutlinedInput label="Mesi" />}
                  renderValue={(selected) => `${selected.length} mesi selezionati`}
                >
                  {allMesi.map((mese) => (
                    <MenuItem key={mese} value={mese}>
                      <Checkbox checked={selectedMesi.indexOf(mese) > -1} />
                      <ListItemText primary={MESI_LABELS[mese]} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllMesi}
                  sx={{ textTransform: 'none' }}
                >
                  Seleziona tutti
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDeselectAllMesi}
                  sx={{ textTransform: 'none' }}
                >
                  Deseleziona tutti
                </Button>
              </Box>
            </Box>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Struttura</InputLabel>
              <Select
                value={selectedStruttura || ''}
                label="Struttura"
                onChange={(e) => handleStrutturaFilterChange(e.target.value as string)}
              >
                <MenuItem value="">Tutte le strutture</MenuItem>
                {strutture.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Anno</InputLabel>
              <Select
                value={selectedYear}
                label="Anno"
                onChange={(e) => setSelectedYear(e.target.value as number)}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Messaggio quando non ci sono dati */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Alert severity="info" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Nessun dato da visualizzare
            </Typography>
            <Typography variant="body2">
              Seleziona almeno una risorsa e un mese per visualizzare la dashboard.
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  // Prepara dati per i grafici
  // Calcola il totale delle allocazioni per ottenere percentuali corrette
  const totaleAllocazioni = dashboardData.top_10_progetti.reduce(
    (sum, p) => sum + parseFloat(p.totale_allocazione.toString()),
    0
  );

  const pieChartData = dashboardData.top_10_progetti.map(p => {
    const allocazione = parseFloat(p.totale_allocazione.toString());
    const percentuale = totaleAllocazioni > 0 ? (allocazione / totaleAllocazioni) * 100 : 0;
    return {
      id: p.id,
      name: p.titolo.length > 20 ? p.titolo.substring(0, 20) + '...' : p.titolo,
      value: percentuale,
      fullName: p.titolo,
      allocazioneOriginale: allocazione
    };
  });

  // Prepara dati per il grafico a linee (andamento mensile)
  const lineChartData = dashboardData.andamento_mensile.map(am => ({
    mese: MESI_LABELS[am.mese],
    allocazione: parseFloat(am.allocazione_media.toString())
  }));

  // Prepara dati per il grafico a barre (distribuzione struttura)
  const barChartData = dashboardData.distribuzione_struttura.map(ds => ({
    struttura: ds.struttura,
    risorse: parseInt(ds.numero_risorse.toString())
  }));

  // Prepara dati per la tabella pianificazione
  const mesiPresenti = Array.from(
    new Set(dashboardData.pianificazione_dettagliata.filter(p => p.mese).map(p => p.mese!))
  ).sort();

  // Organizza i dati per risorsa e progetto
  const pianificazioneGrouped = dashboardData.pianificazione_dettagliata.reduce((acc, row) => {
    if (!row.id_progetto) return acc;

    const key = `${row.id_risorsa}-${row.id_progetto}`;
    if (!acc[key]) {
      acc[key] = {
        cognome: row.cognome,
        nome: row.nome,
        struttura: row.struttura,
        progetto_titolo: row.progetto_titolo,
        pm: row.pm,
        cliente: row.cliente,
        allocazioni: {} as { [mese: number]: number }
      };
    }
    if (row.mese && row.percentuale !== null) {
      acc[key].allocazioni[row.mese] = row.percentuale;
    }
    return acc;
  }, {} as any);

  // Filtra per progetto selezionato se presente
  let pianificazioneArray = Object.values(pianificazioneGrouped);
  if (selectedProgetto) {
    pianificazioneArray = pianificazioneArray.filter((row: any) => {
      // Trova l'id del progetto dal titolo nel dashboard data
      const progetto = dashboardData.top_10_progetti.find(p => p.id === selectedProgetto);
      return progetto && row.progetto_titolo === progetto.titolo;
    });
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con filtri anno, mesi e risorse */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Dashboard Dirigenziale
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Risorse</InputLabel>
              <Select
                multiple
                value={selectedRisorse}
                onChange={handleRisorseChange}
                input={<OutlinedInput label="Risorse" />}
                renderValue={(selected) => `${selected.length} risorse selezionate`}
              >
                {risorse.map((risorsa) => (
                  <MenuItem key={risorsa.id} value={risorsa.id}>
                    <Checkbox checked={selectedRisorse.indexOf(risorsa.id) > -1} />
                    <ListItemText primary={`${risorsa.cognome} ${risorsa.nome}`} secondary={risorsa.struttura} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                sx={{ textTransform: 'none' }}
              >
                Seleziona tutti
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDeselectAll}
                sx={{ textTransform: 'none' }}
              >
                Deseleziona tutti
              </Button>
            </Box>
          </Box>
          <Box>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Mesi</InputLabel>
              <Select
                multiple
                value={selectedMesi}
                onChange={handleMesiChange}
                input={<OutlinedInput label="Mesi" />}
                renderValue={(selected) => `${selected.length} mesi selezionati`}
              >
                {allMesi.map((mese) => (
                  <MenuItem key={mese} value={mese}>
                    <Checkbox checked={selectedMesi.indexOf(mese) > -1} />
                    <ListItemText primary={MESI_LABELS[mese]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAllMesi}
                sx={{ textTransform: 'none' }}
              >
                Seleziona tutti
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDeselectAllMesi}
                sx={{ textTransform: 'none' }}
              >
                Deseleziona tutti
              </Button>
            </Box>
          </Box>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Struttura</InputLabel>
            <Select
              value={selectedStruttura || ''}
              label="Struttura"
              onChange={(e) => handleStrutturaFilterChange(e.target.value as string)}
            >
              <MenuItem value="">Tutte le strutture</MenuItem>
              {strutture.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={selectedYear}
              label="Anno"
              onChange={(e) => setSelectedYear(e.target.value as number)}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Allocazione Media
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {formatAllocazione(dashboardData.allocazione_media)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={dashboardData.allocazione_media >= 80 ? 'Ottima' : dashboardData.allocazione_media >= 60 ? 'Buona' : 'Da Migliorare'}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <People sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Risorse Attive
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {dashboardData.statistiche.totale_risorse}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                {dashboardData.distribuzione_struttura.length} strutture
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FolderOpen sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Progetti in uso
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {dashboardData.statistiche.progetti_in_uso}
                <Typography component="span" variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', ml: 0.5 }}>
                  /{dashboardData.statistiche.totale_progetti}
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                pianificati o rendicontati nel {selectedYear}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Assessment sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Allocazioni Totali
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {dashboardData.statistiche.totale_allocazioni}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                Anno {selectedYear}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grafici */}
      <Grid container spacing={3}>
        {/* Grafico a Torta - Top 10 Progetti */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: 450 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                Top 10 Progetti per Allocazione
              </Typography>
              {selectedProgetto && (
                <Chip
                  label={`Filtro: ${pieChartData.find(p => p.id === selectedProgetto)?.fullName || 'Progetto'}`}
                  onDelete={() => setSelectedProgetto(null)}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => {
                      const displayName = name.length > 15 ? name.substring(0, 15) + '...' : name;
                      return `${displayName} ${(percent * 100).toFixed(1)}%`;
                    }}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={handleProgettoClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.id === selectedProgetto ? '#ff8042' : COLORS[index % COLORS.length]}
                        stroke={entry.id === selectedProgetto ? '#000' : 'none'}
                        strokeWidth={entry.id === selectedProgetto ? 2 : 0}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, _name: string, props: any) => {
                      return [
                        `${value.toFixed(1)}%`,
                        props.payload.fullName
                      ];
                    }}
                    contentStyle={{ fontSize: '12px' }}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value: string, entry: any) => {
                      const percentage = entry.payload.value.toFixed(1);
                      return `${value} (${percentage}%)`;
                    }}
                    wrapperStyle={{ fontSize: '11px', paddingLeft: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
                <Typography color="text.secondary">Nessun dato disponibile</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Grafico a Linee - Andamento Mensile */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: 450 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Andamento Allocazione Mensile
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {lineChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={lineChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mese" />
                  <YAxis domain={[0, 100]} label={{ value: '%', position: 'insideLeft' }} />
                  <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="allocazione"
                    stroke="#8884d8"
                    strokeWidth={3}
                    name="Allocazione Media"
                    dot={{ r: 6 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
                <Typography color="text.secondary">Nessun dato disponibile</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Grafico a Barre - Distribuzione Risorse per Struttura */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                Distribuzione Risorse per Struttura
              </Typography>
              {selectedStruttura && (
                <Chip
                  label={`Filtro: ${selectedStruttura}`}
                  onDelete={() => {
                    setSelectedStruttura(null);
                    setSelectedRisorse(risorse.map(r => r.id));
                  }}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="struttura" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="risorse"
                    name="Numero Risorse"
                    onClick={handleStrutturaClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {barChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.struttura === selectedStruttura ? '#ff8042' : '#82ca9d'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
                <Typography color="text.secondary">Nessun dato disponibile</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Tabella - Budget Residuo Top 5 */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Top 5 Progetti per Budget Residuo
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {dashboardData.budget_residuo_top.length > 0 ? (
              <Box sx={{ overflowY: 'auto', maxHeight: 320 }}>
                {dashboardData.budget_residuo_top.map((progetto, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      mb: 1,
                      backgroundColor: index % 2 === 0 ? '#f5f5f5' : 'white',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {progetto.titolo}
                      </Typography>
                      {progetto.cliente && (
                        <Typography variant="body2" color="text.secondary">
                          {progetto.cliente}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      icon={<AccountBalance />}
                      label={`${progetto.budget_residuo_gg_uu} gg/uu`}
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                <Typography color="text.secondary">Nessun progetto con budget residuo</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabella Pianificazione Dettagliata */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Pianificazione Dettagliata Risorse Selezionate
          </Typography>
          {selectedProgetto && (
            <Chip
              label={`Filtro Progetto: ${pieChartData.find(p => p.id === selectedProgetto)?.fullName || 'Progetto'}`}
              onDelete={() => setSelectedProgetto(null)}
              color="secondary"
              size="small"
            />
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        {pianificazioneArray.length > 0 ? (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Cognome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Struttura</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Progetto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>PM</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Cliente</TableCell>
                  {mesiPresenti.map(mese => (
                    <TableCell
                      key={mese}
                      align="center"
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 60 }}
                    >
                      {MESI_LABELS[mese]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pianificazioneArray.map((row: any, index: number) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <TableCell>{row.cognome}</TableCell>
                    <TableCell>{row.nome}</TableCell>
                    <TableCell>{row.struttura}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={row.progetto_titolo}>
                        {row.progetto_titolo}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.pm || '-'}</TableCell>
                    <TableCell>{row.cliente || '-'}</TableCell>
                    {mesiPresenti.map(mese => (
                      <TableCell key={mese} align="center">
                        {row.allocazioni[mese] ? (
                          <Chip
                            label={`${row.allocazioni[mese]}%`}
                            size="small"
                            color={
                              row.allocazioni[mese] >= 80
                                ? 'success'
                                : row.allocazioni[mese] >= 50
                                ? 'primary'
                                : 'default'
                            }
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography color="text.secondary">
              Nessuna pianificazione disponibile per le risorse selezionate
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardPage;
